import { useItemList } from "@/hooks/useItemList";
import type { Item } from "@/utils/itemclass";
import { useMemo, useState } from "react";
import FilterBar from "@/ui/components/FilterBar";
import PageHeader from "@/ui/components/PageHeader";
import ProfitTable from "@/ui/components/ProfitTable";
import Coins from "@/ui/components/Coins";
import MarketplaceTaxInput from "@/ui/components/MarketplaceTaxInput";
import { DEFAULT_MARKETPLACE_TAX_PERCENT } from "@/utils/marketplaceTax";

export default function TransmutePage() {
  const [filter, setFilter] = useState("");
  const [marketplaceTaxPercent, setMarketplaceTaxPercent] = useState(
    DEFAULT_MARKETPLACE_TAX_PERCENT,
  );
  const { entries } = useItemList("/api/transmute/profit");

  const filteredAndSorted = useMemo(() => {
    const sorted = [...entries].sort(
      (a, b) =>
        b.getTransmuteOutputValueAfterTax(marketplaceTaxPercent) -
        b.buy -
        (a.getTransmuteOutputValueAfterTax(marketplaceTaxPercent) - a.buy),
    );

    if (filter.trim() === "") return sorted;

    return sorted.filter((entry) =>
      entry.name.toLowerCase().includes(filter.toLowerCase()),
    );
  }, [entries, filter, marketplaceTaxPercent]);

  return (
    <div className="mt-4 px-4 sm:px-6 lg:px-8">
      <PageHeader
        title="Transmute Profit Calculator"
        description="This list shows the potential profits for each item when purchased from the marketplace and transmuted."
      />
      {/* MARKETPLACE */}
      <div className="mt-8">
        <MarketplaceTaxInput
          value={marketplaceTaxPercent}
          onChange={setMarketplaceTaxPercent}
        />
      </div>

      {/* FILTER */}
      <FilterBar
        value={filter}
        onChange={setFilter}
        datalistEntries={entries.map((entry) => ({
          key: entry.item,
          label: entry.name,
        }))}
        showSortButton={false}
      />

      {/** TABLE */}
      <ProfitTable
        columns={[
          "Item",
          "Transmute Profit",
          "Market Buy",
          "Market Sell",
          "Buy/Transmute Threshold",
          "Transmute Threshold",
        ]}
      >
        {filteredAndSorted.map((item: Item) => {
          if (item.transmute_profit && item.buy > 0 && item.sell > 0) {
            const outputValueAfterTax =
              item.getTransmuteOutputValueAfterTax(marketplaceTaxPercent);

            return (
              <tr key={item.item}>
                <td className="px-3 py-4 text-sm whitespace-nowrap text-gray">
                  {item.name}
                </td>
                <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
                  <Coins value={outputValueAfterTax} />
                </td>
                <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
                  <Coins value={item.buy} />
                </td>
                <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
                  <Coins value={item.sell} />
                </td>
                <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
                  <Coins value={outputValueAfterTax - item.buy} />
                </td>
                <td className="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
                  <Coins value={outputValueAfterTax - item.sell} />
                </td>
              </tr>
            );
          }
        })}
      </ProfitTable>
    </div>
  );
}
